// src/components/category/ParameterList.tsx
import React, { useState } from "react";
import { CategoryParameter, ParameterDataType } from "../../types/Category";
import { Unit, UnitDimension, UnitSystem } from "../../types/Unit";
import DoubleUnitsEditor from "./DoubleUnitsEditor";
import { getUserMessage, logApiError } from "../../api/apiError";

interface ParameterListProps {
    parameters: CategoryParameter[];
    units?: Unit[];
    dimensions?: UnitDimension[];
    systems?: UnitSystem[];
    onAdd: () => void;
    onRemove: (index: number) => void;
    onChange: (
        index: number,
        field: keyof CategoryParameter | "options",
        value: string | ParameterDataType | string[]
    ) => void;

    onSaveNameUpdate?: (
        index: number,
        parameter: CategoryParameter,
        newName: string
    ) => Promise<void>;

    onSaveDescriptionUpdate?: (
        index: number,
        parameter: CategoryParameter,
        newDescription: string
    ) => Promise<void>;

    onSaveUnitUpdate?: (
        index: number,
        parameter: CategoryParameter,
        newUnitIds: string[]
    ) => Promise<void>;

    onSaveOptionEdit?: (
        paramIndex: number,
        choiceId: number,
        newName: string
    ) => Promise<void>;

    onRemoveOption?: (
        paramIndex: number,
        choiceId: number
    ) => Promise<void>;

    /** Moves a parameter up (-1) or down (+1) in the display order — the
     *  order used by search, listing forms, and outreach features. */
    onMove?: (index: number, delta: -1 | 1) => Promise<void>;

    disabled?: boolean;
}

type EditableField = "name" | "description" | "unit" | null;

const ParameterList: React.FC<ParameterListProps> = ({
                                                         parameters,
                                                         units = [],
                                                         dimensions = [],
                                                         systems = [],
                                                         onAdd,
                                                         onRemove,
                                                         onChange,
                                                         onSaveNameUpdate,
                                                         onSaveDescriptionUpdate,
                                                         onSaveUnitUpdate,
                                                         onSaveOptionEdit,
                                                         onRemoveOption,
                                                         onMove,
                                                         disabled = false,
                                                     }) => {
    const [openParameterIndex, setOpenParameterIndex] = useState<number | null>(null);
    const [editingField, setEditingField] = useState<EditableField>(null);
    const [draftValue, setDraftValue] = useState("");
    const [draftUnitIds, setDraftUnitIds] = useState<string[]>([]);
    const [saveError, setSaveError] = useState<string | null>(null);

    const unitName = (uid?: string) =>
        uid ? (units.find(u => u.id === uid)?.name ?? uid) : "";

    // The allowed unit ids for a parameter, regardless of integer/double shape.
    const paramUnitIds = (p: CategoryParameter): string[] =>
        p.dataType === ParameterDataType.DOUBLE
            ? (p.unitIds ?? [])
            : (p.unitId ? [p.unitId] : []);

    const [editingOptionIndex, setEditingOptionIndex] = useState<number | null>(null);
    const [optionDraft, setOptionDraft] = useState("");
    const [optionError, setOptionError] = useState<string | null>(null);
    const [addingOption, setAddingOption] = useState(false);
    const [newOptionValue, setNewOptionValue] = useState("");

    const openParameterEditor = (index: number) => {
        setOpenParameterIndex(openParameterIndex === index ? null : index);
        setEditingField(null);
        setDraftValue("");
        setDraftUnitIds([]);
        setSaveError(null);
        setEditingOptionIndex(null);
        setOptionDraft("");
        setOptionError(null);
        setAddingOption(false);
        setNewOptionValue("");
    };

    const startFieldEdit = (field: "name" | "description" | "unit", value: string) => {
        setEditingField(field);
        setDraftValue(value);
        setSaveError(null);
    };

    const startUnitEdit = (parameter: CategoryParameter) => {
        setEditingField("unit");
        setDraftUnitIds(paramUnitIds(parameter));
        setSaveError(null);
    };

    const cancelFieldEdit = () => {
        setEditingField(null);
        setDraftValue("");
        setDraftUnitIds([]);
        setSaveError(null);
    };

    const saveFieldEdit = async (index: number, parameter: CategoryParameter) => {
        if (!editingField) {
            return;
        }

        const trimmedValue = draftValue.trim();

        try {
            if (editingField === "name") {
                if (!trimmedValue) {
                    setSaveError("Parameter name is required.");
                    return;
                }
                await onSaveNameUpdate?.(index, parameter, trimmedValue);
            }

            if (editingField === "description") {
                await onSaveDescriptionUpdate?.(index, parameter, trimmedValue);
            }

            if (editingField === "unit") {
                await onSaveUnitUpdate?.(index, parameter, draftUnitIds);
            }

            setEditingField(null);
            setDraftValue("");
            setDraftUnitIds([]);
            setSaveError(null);
        } catch (err) {
            logApiError(err);
            setSaveError(getUserMessage(err));
        }
    };

    return (
        <div className="parameter-editor-list">
            <div className="parameter-editor-list-header" style={{ borderBottom: "2px solid #94a3b8", paddingBottom: "0.5rem", marginBottom: "0.5rem" }}>
                <h4 style={{ margin: 0 }}>Parameters</h4>
            </div>

            {parameters.length === 0 && (
                <div className="parameter-editor-empty">No parameters yet.</div>
            )}

            {parameters.map((p, i) => {
                const isOpen = openParameterIndex === i;
                const selectionType = (p as any).selectionType as string | undefined;

                return (
                    <div key={i} className="parameter-editor-row-wrapper" style={{
                        border: isOpen ? "1.5px solid #94a3b8" : "none",
                        borderBottom: "1.5px solid #94a3b8",
                        borderRadius: isOpen ? "var(--radius-md)" : 0,
                        padding: isOpen ? "0.75rem" : "0.4rem 0",
                        marginBottom: isOpen ? "0.75rem" : 0,
                        background: isOpen ? "var(--bg-secondary)" : "transparent",
                        transition: "background 0.15s",
                    }}>
                        {/* ── Summary row ── */}
                        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.4rem 0" }}>
                            <span style={{ flex: "1 1 120px", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</span>
                            <span style={{ flex: "2 1 0", fontSize: "0.85rem", color: "var(--text-secondary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.description || "—"}</span>
                            <span style={{ flex: "0 0 auto", fontSize: "0.75rem", background: "var(--bg-secondary)", padding: "2px 7px", borderRadius: "var(--radius-sm)", whiteSpace: "nowrap" }}>{p.dataType}</span>
                            {(p.dataType === ParameterDataType.INTEGER || p.dataType === ParameterDataType.DOUBLE) && paramUnitIds(p).length > 0 && (
                                <span style={{ flex: "0 0 auto", fontSize: "0.75rem", color: "var(--text-secondary)", whiteSpace: "nowrap" }}>
                                    {paramUnitIds(p).map(unitName).join(", ")}
                                </span>
                            )}
                            {onMove && (
                                <>
                                    <button type="button" className="btn-sm" onClick={() => onMove(i, -1)}
                                            disabled={disabled || i === 0} title="Move up" style={{ flex: "0 0 auto" }}>
                                        ↑
                                    </button>
                                    <button type="button" className="btn-sm" onClick={() => onMove(i, 1)}
                                            disabled={disabled || i === parameters.length - 1} title="Move down" style={{ flex: "0 0 auto" }}>
                                        ↓
                                    </button>
                                </>
                            )}
                            <button type="button" className="btn-sm" onClick={() => openParameterEditor(i)} disabled={disabled} style={{ flex: "0 0 auto" }}>
                                {isOpen ? "Close" : "Edit"}
                            </button>
                            <button type="button" className="btn-danger btn-sm" onClick={() => onRemove(i)} disabled={disabled} style={{ flex: "0 0 auto" }}>
                                Remove
                            </button>
                        </div>

                        {/* ── SELECTABLE summary (collapsed only) ── */}
                        {p.dataType === ParameterDataType.SELECTABLE && !isOpen && (
                            <div style={{ paddingLeft: "0.75rem", marginBottom: "0.4rem", fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                                <div style={{ marginBottom: "0.2rem" }}>
                                    <strong>Selection:</strong> {selectionType ?? "SINGLE"}
                                </div>
                                <div style={{ display: "flex", flexDirection: "column", gap: "0.15rem" }}>
                                    {(p.options ?? []).length > 0
                                        ? p.options!.map((opt) => <span key={opt}>• {opt}</span>)
                                        : <span style={{ fontStyle: "italic" }}>No choices</span>
                                    }
                                </div>
                            </div>
                        )}

                        {isOpen && (
                            <div className="parameter-edit-panel" style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>

                                {/* Name */}
                                <div>
                                    <label style={{ marginBottom: "0.25rem" }}>Name</label>
                                    <input
                                        className="parameter-editor-input"
                                        style={{ width: "100%", marginBottom: "0.35rem" }}
                                        value={editingField === "name" ? draftValue : p.name}
                                        disabled={disabled || editingField !== "name"}
                                        onChange={(e) => setDraftValue(e.target.value)}
                                    />
                                    <div style={{ display: "flex", gap: "0.4rem" }}>
                                        {editingField === "name" ? (
                                            <>
                                                <button type="button" className="btn-success btn-sm" onClick={() => saveFieldEdit(i, p)} disabled={disabled}>Save</button>
                                                <button type="button" className="btn-secondary btn-sm" onClick={cancelFieldEdit}>Cancel</button>
                                            </>
                                        ) : (
                                            <button type="button" className="btn-sm" onClick={() => startFieldEdit("name", p.name)} disabled={disabled || editingField !== null}>Edit</button>
                                        )}
                                    </div>
                                    {editingField === "name" && saveError && (
                                        <div className="error-message" style={{ marginTop: "0.35rem" }}>{saveError}</div>
                                    )}
                                </div>

                                {/* Type */}
                                <div>
                                    <label style={{ marginBottom: "0.25rem" }}>Type</label>
                                    <input className="parameter-editor-input" value={p.dataType} disabled style={{ width: "100%" }} />
                                </div>

                                {/* Unit — INTEGER: single optional unit; DOUBLE: one or more units */}
                                {p.dataType === ParameterDataType.INTEGER && (
                                    <div>
                                        <label style={{ marginBottom: "0.25rem" }}>Unit</label>
                                        {editingField === "unit" ? (
                                            <>
                                                <select
                                                    className="parameter-editor-input"
                                                    style={{ width: "100%", marginBottom: "0.35rem" }}
                                                    value={draftUnitIds[0] ?? ""}
                                                    onChange={(e) => setDraftUnitIds(e.target.value ? [e.target.value] : [])}
                                                    disabled={disabled}
                                                >
                                                    <option value="">— none —</option>
                                                    {units.map((u) => (
                                                        <option key={u.id} value={u.id}>{u.name}</option>
                                                    ))}
                                                </select>
                                                <div style={{ display: "flex", gap: "0.4rem" }}>
                                                    <button type="button" className="btn-success btn-sm" onClick={() => saveFieldEdit(i, p)} disabled={disabled}>Save</button>
                                                    <button type="button" className="btn-secondary btn-sm" onClick={cancelFieldEdit}>Cancel</button>
                                                </div>
                                                {saveError && (
                                                    <div className="error-message" style={{ marginTop: "0.35rem" }}>{saveError}</div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <input
                                                    className="parameter-editor-input"
                                                    style={{ width: "100%", marginBottom: "0.35rem" }}
                                                    value={p.unitId ? unitName(p.unitId) : "—"}
                                                    disabled
                                                />
                                                <button type="button" className="btn-sm" onClick={() => startUnitEdit(p)} disabled={disabled || editingField !== null}>Edit</button>
                                            </>
                                        )}
                                    </div>
                                )}

                                {p.dataType === ParameterDataType.DOUBLE && (
                                    <div>
                                        <label style={{ marginBottom: "0.25rem" }}>Units (same dimension, one per system)</label>
                                        {editingField === "unit" ? (
                                            <>
                                                <div style={{ marginBottom: "0.35rem" }}>
                                                    <DoubleUnitsEditor
                                                        units={units}
                                                        dimensions={dimensions}
                                                        systems={systems}
                                                        value={draftUnitIds}
                                                        onChange={setDraftUnitIds}
                                                        disabled={disabled}
                                                    />
                                                </div>
                                                <div style={{ display: "flex", gap: "0.4rem" }}>
                                                    <button type="button" className="btn-success btn-sm" onClick={() => saveFieldEdit(i, p)} disabled={disabled}>Save</button>
                                                    <button type="button" className="btn-secondary btn-sm" onClick={cancelFieldEdit}>Cancel</button>
                                                </div>
                                                {saveError && (
                                                    <div className="error-message" style={{ marginTop: "0.35rem" }}>{saveError}</div>
                                                )}
                                            </>
                                        ) : (
                                            <>
                                                <input
                                                    className="parameter-editor-input"
                                                    style={{ width: "100%", marginBottom: "0.35rem" }}
                                                    value={paramUnitIds(p).length > 0 ? paramUnitIds(p).map(unitName).join(", ") : "—"}
                                                    disabled
                                                />
                                                <button type="button" className="btn-sm" onClick={() => startUnitEdit(p)} disabled={disabled || editingField !== null}>Edit</button>
                                            </>
                                        )}
                                    </div>
                                )}

                                {/* Selection Type (SELECTABLE only) */}
                                {p.dataType === ParameterDataType.SELECTABLE && (
                                    <div>
                                        <label style={{ marginBottom: "0.25rem" }}>Selection Type</label>
                                        <input className="parameter-editor-input" value={selectionType ?? "SINGLE"} disabled style={{ width: "100%" }} />
                                    </div>
                                )}

                                {/* Description */}
                                <div>
                                    <label style={{ marginBottom: "0.25rem" }}>Description</label>
                                    <textarea
                                        className="parameter-editor-input"
                                        style={{ width: "100%", resize: "vertical", minHeight: "4rem", maxHeight: "10rem", overflowY: "auto", marginBottom: "0.35rem" }}
                                        value={editingField === "description" ? draftValue : p.description ?? ""}
                                        disabled={disabled || editingField !== "description"}
                                        onChange={(e) => setDraftValue(e.target.value)}
                                    />
                                    <div style={{ display: "flex", gap: "0.4rem" }}>
                                        {editingField === "description" ? (
                                            <>
                                                <button type="button" className="btn-success btn-sm" onClick={() => saveFieldEdit(i, p)} disabled={disabled}>Save</button>
                                                <button type="button" className="btn-secondary btn-sm" onClick={cancelFieldEdit}>Cancel</button>
                                            </>
                                        ) : (
                                            <button type="button" className="btn-sm" onClick={() => startFieldEdit("description", p.description ?? "")} disabled={disabled || editingField !== null}>Edit</button>
                                        )}
                                    </div>
                                    {editingField === "description" && saveError && (
                                        <div className="error-message" style={{ marginTop: "0.35rem" }}>{saveError}</div>
                                    )}
                                </div>

                                {p.dataType === ParameterDataType.SELECTABLE && (
                                    <div style={{ gridColumn: "1 / -1", display: "flex", flexDirection: "column", gap: "0.4rem" }}>
                                        <label>Choices</label>

                                        {(p.options ?? []).map((opt, optIdx) => (
                                            <div key={optIdx} style={{ display: "flex", flexDirection: "column", gap: "0.25rem" }}>
                                                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                                {editingOptionIndex === optIdx ? (
                                                    <>
                                                        <input
                                                            className="parameter-editor-input"
                                                            value={optionDraft}
                                                            onChange={(e) => setOptionDraft(e.target.value)}
                                                            autoFocus
                                                        />
                                                        <button
                                                            type="button"
                                                            className="btn-success btn-sm"
                                                            onClick={async () => {
                                                                const trimmed = optionDraft.trim();
                                                                if (!trimmed) return;
                                                                setOptionError(null);
                                                                try {
                                                                    if (onSaveOptionEdit) {
                                                                        const selectables = (p as any).selectables as Array<{ id: number; name: string }> | undefined;
                                                                        const choice = selectables?.find((s) => s.name === opt);
                                                                        if (choice) {
                                                                            await onSaveOptionEdit(i, choice.id, trimmed);
                                                                            setEditingOptionIndex(null);
                                                                            setOptionDraft("");
                                                                            return;
                                                                        }
                                                                    }
                                                                    const next = [...(p.options ?? [])];
                                                                    next[optIdx] = trimmed;
                                                                    onChange(i, "options", next);
                                                                    setEditingOptionIndex(null);
                                                                    setOptionDraft("");
                                                                } catch (err) {
                                                                    logApiError(err);
                                                                    setOptionError(getUserMessage(err));
                                                                }
                                                            }}
                                                            disabled={disabled}
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn-secondary btn-sm"
                                                            onClick={() => { setEditingOptionIndex(null); setOptionDraft(""); setOptionError(null); }}
                                                        >
                                                            Cancel
                                                        </button>
                                                    </>
                                                ) : (
                                                    <>
                                                        <span className="parameter-editor-input" style={{ display: "inline-block" }}>{opt}</span>
                                                        <button
                                                            type="button"
                                                            className="btn-sm"
                                                            onClick={() => { setEditingOptionIndex(optIdx); setOptionDraft(opt); setOptionError(null); }}
                                                            disabled={disabled || editingOptionIndex !== null || addingOption}
                                                        >
                                                            Edit
                                                        </button>
                                                        <button
                                                            type="button"
                                                            className="btn-danger btn-sm"
                                                            onClick={async () => {
                                                                setOptionError(null);
                                                                try {
                                                                    if (onRemoveOption) {
                                                                        const selectables = (p as any).selectables as Array<{ id: number; name: string }> | undefined;
                                                                        const choice = selectables?.find((s) => s.name === opt);
                                                                        if (choice) {
                                                                            await onRemoveOption(i, choice.id);
                                                                            return;
                                                                        }
                                                                    }
                                                                    const next = (p.options ?? []).filter((_, idx) => idx !== optIdx);
                                                                    onChange(i, "options", next);
                                                                } catch (err) {
                                                                    logApiError(err);
                                                                    setOptionError(getUserMessage(err));
                                                                }
                                                            }}
                                                            disabled={disabled || editingOptionIndex !== null || addingOption}
                                                        >
                                                            Remove
                                                        </button>
                                                    </>
                                                )}
                                                </div>
                                                {editingOptionIndex === optIdx && optionError && (
                                                    <div className="error-message" style={{ marginTop: "0.1rem" }}>{optionError}</div>
                                                )}
                                            </div>
                                        ))}

                                        {addingOption ? (
                                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                                <input
                                                    className="parameter-editor-input"
                                                    value={newOptionValue}
                                                    onChange={(e) => setNewOptionValue(e.target.value)}
                                                    placeholder="New option"
                                                    autoFocus
                                                />
                                                <button
                                                    type="button"
                                                    className="btn-success btn-sm"
                                                    onClick={() => {
                                                        const trimmed = newOptionValue.trim();
                                                        if (!trimmed) return;
                                                        const next = [...(p.options ?? []), trimmed];
                                                        onChange(i, "options", next);
                                                        setNewOptionValue("");
                                                        setAddingOption(false);
                                                    }}
                                                    disabled={disabled}
                                                >
                                                    Add
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn-secondary btn-sm"
                                                    onClick={() => { setAddingOption(false); setNewOptionValue(""); }}
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
                                                <button
                                                    type="button"
                                                    className="btn-sm"
                                                    onClick={() => setAddingOption(true)}
                                                    disabled={disabled || editingOptionIndex !== null}
                                                >
                                                    + Add Option
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div style={{ gridColumn: "1 / -1", marginTop: "0.5rem", paddingTop: "0.75rem", borderTop: "1px solid var(--border-color)" }}>
                                    <button
                                        type="button"
                                        className="btn-sm btn-secondary"
                                        onClick={() => openParameterEditor(i)}
                                    >
                                        Done
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

export default ParameterList;